// AIドラフト検証（架空ケース）の API 型。既存の督促 screen API と同じく、
// リクエスト・レスポンスともキーはキャメルケース
import type {
  ContactCounterpart,
  ContactDetail,
  ContactDirection,
  ContactOutcome,
  ContactPhoneLine,
  ContactResult,
} from "../types"
import dayjs from "dayjs"
import { isPromiseScheduleActive } from "../components/shared"
import type { Coverage, Expectation } from "./expectation"
import { buildContract, scenarioOverdueDays } from "./installmentPlan"
import { UNSET_TERMINAL, isUnsetTerminal, terminalShortLabel } from "../terminals"

export type ScenarioMessage = {
  at: string
  direction: "inbound" | "outbound"
  body: string
  contentType: "text" | "image"
}

/** 登録元（本番の支払約束の source） */
export type PromiseSource = "dunning_payment_promise" | "bank_transfer_invoice"

/** 有効な支払約束（本番の AI 入力の active_promise）。期日は基準日時以降 */
export type ScenarioActivePromise = {
  scheduledOn: string
  amount: number | null
  source: PromiseSource | null
}

/** 約束の履歴（本番の AI 入力の promise_history）。結果は支払済みか（fulfilled）で持ち、期日を過ぎて未払いなら不履行 */
export type ScenarioPromiseHistoryEntry = {
  promisedAt: string
  scheduledOn: string
  amount: number | null
  fulfilled: boolean
  source?: PromiseSource | null
  /** 今回の延滞より前の約束（AI 入力で「［今回の延滞より前］」の印が付く） */
  previousDelinquency?: boolean | null
}

/** LINE 以外の交渉記録（本番の dunning_contact_logs と同じ項目・選択肢） */
export type ScenarioContactLog = {
  contactedAt: string
  channel: "phone" | "sms" | "email" | "in_person" | "other"
  direction: ContactDirection
  counterpart?: ContactCounterpart | null
  contactResult?: ContactResult | null
  contactDetail?: ContactDetail | null
  /** 旧「結果」（旧レコードを読み込んだときだけ入る） */
  outcome?: ContactOutcome | null
  phoneLine?: ContactPhoneLine | null
  note?: string | null
  countsAsContact?: boolean | null
}

/** オペレーターメモ（本番のユーザー単位メモ） */
export type ScenarioMemo = { createdAt: string; title?: string | null; body: string }

/** 契約の分割予定1回分（本番の支払いスケジュールと同じ項目） */
export type ScenarioInstallment = {
  numberOfPayment: number | null
  /** 約定日（YYYY-MM-DD） */
  scheduledOn: string
  paymentTotalAmount: number
  paidAmount: number
  lastPaidAt?: string | null
  /** 口座振替の振替日・結果日（YYYY-MM-DD）。振替請求ありなら結果日の13時、なしなら振替日の翌日に延滞確定 */
  transferOn?: string | null
  resultOn?: string | null
  transferRequested?: boolean | null
}

/** 契約1件。残回数・残高は分割予定に書かない回も含めた契約全体の値 */
export type ScenarioContract = {
  propertyName: string | null
  numberOfPayment: number | null
  totalPaymentAmount: number | null
  remainingPaymentCount: number
  remainingBalance: number
  installments: ScenarioInstallment[]
  /** 請求停止の契約（引き落としが掛からない）。あれば AI 入力の契約の見出しの次に「引き落とし停止中（請求停止）」が出る。未指定は請求停止でない */
  billingStopped?: boolean | null
}

/**
 * 検証ケースの AI 入力。本番の AI 入力（PrepareDunningDraftContextUsecase）と同じ項目で持つ。
 * 約束・交渉記録・メモ・契約は省略できる（古いケースは事実メモ facts にまとめて持っている）。
 * 契約があるとき、遅延金額（principalDue）と次回約定日（nextDueOn）は契約の分割予定から導いた値で保存される
 */
export type Scenario = {
  asOf: string
  routingMode: "reactive" | "proactive"
  overdueDays: number
  principalDue: number
  lateChargeDue: number
  payday: number | null
  nextDueOn: string
  companyName: string
  /** 事実メモ（古い形式）。AI には基準日時のオペレーターメモとして渡る */
  facts: string
  conversation: ScenarioMessage[]
  lineBlocked?: boolean | null
  /** これまでの接触回数。未指定なら当社の送信と交渉記録から数える */
  contactCountTotal?: number | null
  /** 今回の延滞の開始。以前の延滞があるケースだけ持ち、AI 入力の会話・交渉記録・約束の履歴に今回と以前の区切りが出る */
  currentDelinquencyStartedAt?: string | null
  /** 今回の延滞の中の接触回数。未指定で接触回数も未指定なら、同じ数え方で区切りより後を数える */
  contactCountCurrent?: number | null
  activePromise?: ScenarioActivePromise | null
  promiseHistory?: ScenarioPromiseHistoryEntry[]
  contactLogs?: ScenarioContactLog[]
  operatorMemos?: ScenarioMemo[]
  contracts?: ScenarioContract[]
  /**
   * 期待する道筋（判断ノード ID の並び。AI には渡さない）。プロンプトの比較で AI の道筋と先頭から比べ、
   * 最初にずれた歩を出す。ノード ID はプロンプトの書き方に合わせた自由な文字列
   */
  expectedPath?: string[]
  /** 振込先（架空の専用口座）。あれば AI 入力に振込先の行が出る（支払案内の書式を評価するため） */
  virtualAccount?: ScenarioVirtualAccount | null
  /** 口座振替の登録。あれば AI 入力に「口座振替: 登録あり」の行が出る。未指定は登録なし */
  bankAccountRegistered?: boolean | null
  /** 架電不可（本人・勤務先・緊急連絡先）。どれかがあれば AI 入力に「架電不可:」の行が出る。未指定は架電可 */
  noCallToUser?: boolean | null
  noCallToWorkplace?: boolean | null
  noCallToEmergencyContact?: boolean | null
}

/** 振込先（専用口座）。支店名が「支店」「出張所」などの種別で終わっていなければ、AI 入力で「支店」が付く */
export type ScenarioVirtualAccount = {
  bankName: string
  branchName: string
  accountNumber: string
  accountHolderName: string
}

/** AI が本文より先に書く判断の1歩（自己申告）。形はサーバーで長さ・必須項目だけ確かめる */
export type DecisionStep = {
  node?: string
  question?: string
  answer?: string
  evidence?: string
  reason?: string
}

/** AI の判断記録（自己申告）。道筋（steps）・AI が選んだ対応（terminal）・まとめ（summary） */
export type Decision = { steps?: DecisionStep[]; terminal?: string; summary?: string }

/** 判断記録が無い版の観測の道筋の1歩 */
export type StrategyPathStep = { nodeId?: string; result?: string }

export type GenerationMetadata = {
  mode?: string
  intent?: string
  humanReviewRequired?: boolean
  humanReviewReason?: string
  decision?: Decision
  strategy?: { terminalId?: string; path?: StrategyPathStep[] }
  strategyObservation?: { status?: string }
}

/**
 * 生成結果。API は評価（rating・reviewNote）も返すが、画面では人の評価を扱わないので型に持たない
 */
export type EvaluationRun = {
  id: number
  status: "running" | "completed" | "failed"
  createdAt: string
  model: string
  promptVersionId: number | null
  durationMs: number | null
  body: string | null
  errorMessage: string | null
  expectedTerminal: string
  caseVersion: number
  checkpoints: string
  generationMetadata: GenerationMetadata | null
}

/** 一覧でケースごとに返る最新の生成の要約（本文はお客様の流れの時点ごとに AI の文面を並べるのに使う） */
export type LatestRun = Pick<
  EvaluationRun,
  "id" | "status" | "createdAt" | "expectedTerminal" | "caseVersion" | "generationMetadata"
> & { body?: string | null }

export type EvaluationCase = {
  id: number
  lockVersion: number
  title: string
  description: string
  expectedTerminal: string
  checkpoints: string
  scenario: Scenario
  /** 一覧のみ。ケースごとの最新の生成の要約（未生成なら null） */
  latestRun?: LatestRun | null
  /** 箱（基本・境界・実データ・個別）。未設定は null。生成時点のケース（caseSnapshot）には無い */
  caseSet?: CaseSet | null
  /** しまった日時。しまったケースは一覧・プロンプトの比較の対象・件数から外す（生成結果は残る） */
  archivedAt?: string | null
  archivedBySmootherId?: number | null
  /** 標準ケース（開発側が反映するケース）。削除できず、しまうだけ */
  standard?: boolean
  /** 続きのケースの元のケース（お客様の流れ。起点は null）。作成時だけ送る */
  parentCaseId?: number | null
  /** 取り込み元の督促ケース（読み込んだケースとその続き）。作成時だけ送り、ユーザーはサーバーが督促ケースから決める */
  sourceDunningCaseId?: number | null
  sourceUserId?: number | null
  /** 期待の形（許す対応・必須要素・禁止表現・長さ・トーン・模範文）。期待の形を足す前のケースは null（期待する対応だけ） */
  expectation?: Expectation | null
  /** カバレッジ表の位置（状況の難しさ）と状況タグ。生成時点のケース（caseSnapshot）には無い */
  coverage?: Coverage | null
}

/**
 * ケースの箱。プロンプトの比較の対象・一覧の絞り込みに使う目印（API の値は変えず、画面の表示名だけを用途で呼ぶ）。
 * 個別は運用が空から作った・読み込んだ・続きのケースの既定で、プロンプトの比較に混ぜない（お客様の流れだけで扱う）
 */
export type CaseSet = "core" | "boundary" | "real" | "individual"

export const CASE_SET_LABELS: Record<CaseSet, string> = {
  core: "回帰テスト",
  boundary: "判断が分かれるケース",
  real: "過去の本番ケース",
  individual: "お客様を追うケース",
}

/** 運用が作るケース（空から・複製・読み込み・続き）の既定の箱 */
export const INDIVIDUAL_CASE_SET: CaseSet = "individual"

export const CASE_SETS = Object.keys(CASE_SET_LABELS) as CaseSet[]

/** しまったケースか */
export const isArchived = (record: EvaluationCase) => !!record.archivedAt

export type RunDetail = EvaluationRun & {
  systemPrompt: string
  userPrompt: string
  caseSnapshot: EvaluationCase
}

/** 期待する対応の ID → 表示名 */
export type Terminals = Record<string, string>

export const terminalOptionLabel = (id: string, label: string) =>
  `${terminalShortLabel(id)}：${label}`

/**
 * 表示名の一覧に無い着地点（プロンプトで足したもの）もまとめられるよう、used（表示中のケースの期待する対応）から拾う
 */
export const terminalGroupOrder = (terminals: Terminals, used: string[]) => {
  const extra = used.filter((id) => !(id in terminals) && !isUnsetTerminal(id))
  return [
    ...Object.keys(terminals).filter((id) => !isUnsetTerminal(id)),
    ...new Set(extra),
    UNSET_TERMINAL,
  ]
}

/** 生成に使うプロンプト。保存済み版を指定するか、本文・モデルを直接渡す */
export type GeneratePrompt = { promptVersionId: number } | { content: string; model: string }

/** 一覧でケースの生成の状態を見分けるための状態（最新の生成） */
export type CaseProgress = "not_generated" | "running" | "failed" | "generated"

export const caseProgress = (record: EvaluationCase): CaseProgress => {
  const run = record.latestRun
  if (!run) return "not_generated"
  return run.status === "completed" ? "generated" : run.status
}

export const PROGRESS_LABELS: Record<CaseProgress, string> = {
  not_generated: "未生成",
  running: "生成中",
  failed: "生成失敗",
  generated: "生成済み",
}

/** 雛形の契約のどの回が過ぎて遅延になるかは、この日時を基準に決まる */
const EMPTY_AS_OF = "2026-09-19T12:00:00+09:00"

/**
 * 空から作るケースの雛形。契約は分割の予定をまとめて作るのと同じ形（8月分の1回が遅延）で入れ、
 * 遅延金額・次回約定日・遅延日数はその分割予定から導いた値にそろえる
 */
export const emptyCase = (): EvaluationCase => {
  const contract = buildContract(
    {
      count: 12,
      amountMode: "monthly",
      amount: 30_000,
      dueDay: 27,
      firstMonth: "2026-03",
      status: "from",
      overdueFrom: 6,
    },
    EMPTY_AS_OF
  )
  const scenario: Scenario = {
    asOf: EMPTY_AS_OF,
    routingMode: "reactive",
    overdueDays: 0,
    principalDue: 30000,
    lateChargeDue: 0,
    payday: null,
    nextDueOn: "2026-09-27",
    companyName: "",
    // 事実メモ（古い形式）は使わない。欄の無い事実はメモ欄に書く
    facts: "",
    operatorMemos: [],
    contracts: [contract],
    conversation: [
      { at: "2026-09-19T10:00:00+09:00", direction: "inbound", contentType: "text", body: "" },
    ],
  }
  return {
    id: 0,
    lockVersion: 0,
    title: "新しい検証ケース",
    description: "",
    expectedTerminal: "F",
    checkpoints: "",
    caseSet: INDIVIDUAL_CASE_SET,
    scenario: { ...scenario, overdueDays: scenarioOverdueDays(scenario) },
  }
}

/**
 * 既存の「複製」と同じ下書き（新規・タイトルに（コピー）・個別の箱）。生成履歴は引き継がない。
 * 流れにはつながない（新しい流れの起点になる）が、取り込み元のお客様は引き継ぐ
 */
export const copyOf = (record: EvaluationCase): EvaluationCase => ({
  ...record,
  id: 0,
  lockVersion: 0,
  latestRun: null,
  caseSet: INDIVIDUAL_CASE_SET,
  parentCaseId: null,
  archivedAt: null,
  archivedBySmootherId: null,
  standard: false,
  title: `${record.title}（コピー）`.slice(0, 150),
})

/** 実在のお客様（督促ケースかユーザー）。どちらか一方の ID で指定する */
export type ImportCustomer = { dunningCaseId: number } | { userId: number }

/**
 * 督促ケースの読み込み元。
 * - お客様＋基準日時（asOf。省略は今）: その時点の状態に組み立て直して読み込む
 * - 生成リクエスト ID: 本番で AI ドラフトを生成したときの入力をそのまま読み込む
 * caseSet は保存するときの箱（読み込み結果にそのまま返る）
 */
export type ImportSource = (
  (ImportCustomer & { asOf?: string }) | { generationRequestId: number }
) & { caseSet?: CaseSet }

/** 読み込んだケースを入れる箱の既定。実在のお客様を時点ごとに追う「個別」に入れ、お客様の流れに出す */
export const IMPORT_CASE_SET: CaseSet | null = INDIVIDUAL_CASE_SET

/** 架空値に置き換えた個人情報の種類と件数 */
export type MaskedItem = { label: string; replacement: string; count: number }

/** 督促ケースから組み立てた検証ケースの下書き（未保存） */
export type ImportPreview = {
  sourceDunningCaseId: number
  sourceUserId: number
  /** 本番の生成リクエストから読み込んだときのその ID */
  sourceGenerationRequestId?: number | null
  title: string
  description: string
  scenario: Scenario
  maskedItems: MaskedItem[]
  notes: string[]
  caseSet?: CaseSet | null
  /** 基準日時を指定したとき、その時点に戻せず今の値のままの項目 */
  keptCurrentItems?: string[]
  /** 基準日時を指定したとき、その時点の直後（24時間以内）にオペレーターが送ったテキストの返信を模範文にした期待（無ければ null） */
  expectation?: Expectation | null
}

/** 読み込む時点を選ぶための一覧（会話は架空値に置き換えた抜粋・古い順、生成リクエストは新しい順） */
export type ImportTimeline = {
  sourceDunningCaseId: number
  messages: { at: string; direction: "inbound" | "outbound"; body: string }[]
  generationRequests: {
    id: number
    promptVersion: number | null
    status: string
    createdAt: string
  }[]
}

/** 読み込んだ下書きを作成フォームに入れる形にする。期待する対応・確認ポイントは運用が付ける */
export const draftFromImport = (preview: ImportPreview): EvaluationCase => ({
  id: 0,
  lockVersion: 0,
  title: preview.title,
  description: preview.description,
  expectedTerminal: "",
  checkpoints: "",
  caseSet: preview.caseSet ?? INDIVIDUAL_CASE_SET,
  sourceDunningCaseId: preview.sourceDunningCaseId,
  sourceUserId: preview.sourceUserId,
  scenario: preview.scenario,
  // 期待する対応はまだ選んでいないので許す対応は空にし、選んだときに先頭へ入る
  ...(preview.expectation
    ? { expectation: { strategy: { terminals: [] }, ...preview.expectation } }
    : {}),
})

/** 日時（ISO 8601）を ms だけずらして、日本時間の ISO 8601（+09:00）で返す */
export const shiftIso = (iso: string, ms: number) =>
  `${new Date(Date.parse(iso) + ms + 9 * 60 * 60 * 1000).toISOString().slice(0, 19)}+09:00`

/** 次の時点（2往復目）の元になったケースと生成（生成結果を使わずに作ったときは runId が null） */
export type ContinuationInfo = {
  parentId: number
  parentTitle: string
  runId: number | null
  /** 書き込んでもらうお客様の返事の発言番号（1始まり） */
  replyNumber: number
}

/**
 * 当社の AIドラフトからお客様の返事までの間隔（返事の日時・新しい基準日時の初期値）。
 * 続きのケースを重ねるたびに基準日時がこの分だけ進むので、何往復か重ねても半日・日をまたがない短さにする
 */
export const REPLY_DELAY_MS = 10 * 60 * 1000

/** Date を API と同じ日本時間の ISO8601（+09:00）にする */
export const toJstIso = (time: number) =>
  `${new Date(time + 9 * 60 * 60 * 1000).toISOString().slice(0, 19)}+09:00`

/**
 * 続きのケースに引き継ぐ事実。新しい基準日時までに期日を過ぎた（23:59 の約束はその日を過ぎた）有効な約束は、本番と同じく
 * 有効な約束から外れて約束の履歴（未払い＝不履行）に入る。接触回数を指定していれば当社の送信1回分を足す
 */
const carriedOver = (scenario: Scenario, asOf: string): Scenario => {
  const active = scenario.activePromise
  const expired = !!active && !isPromiseScheduleActive(dayjs(active.scheduledOn), dayjs(asOf))
  return {
    ...scenario,
    ...(expired && active
      ? {
          activePromise: null,
          promiseHistory: [
            ...(scenario.promiseHistory ?? []),
            {
              promisedAt: scenario.asOf,
              scheduledOn: active.scheduledOn,
              amount: active.amount,
              fulfilled: false,
              source: active.source,
            },
          ].slice(-10),
        }
      : {}),
    ...(scenario.contactCountTotal != null
      ? { contactCountTotal: scenario.contactCountTotal + 1 }
      : {}),
  }
}

/** 続きのケースのタイトルに付ける時点の番号（「…（3時点目）」）。旧形式の「（続き）」も外して数え直す */
const POINT_SUFFIX = /(?:（続き）|（(\d+)時点目）)+$/

export const continuationTitle = (parentTitle: string) => {
  const match = parentTitle.match(POINT_SUFFIX)
  const base = match ? parentTitle.slice(0, match.index) : parentTitle
  const parentPoint = match?.[1] ? Number(match[1]) : 1
  const suffix = `（${parentPoint + 1}時点目）`
  return `${base.slice(0, 150 - suffix.length)}${suffix}`
}

/**
 * 「次の時点」（子ケース）の下書きを作る。生成結果があれば生成時のケースの会話の末尾に、
 * AIドラフトを当社の送信（元の基準日時）として足す。生成結果が無ければ当社の送信を空欄で置き、運用が書き込む。
 * その10分後にお客様の返事（空欄）を置いて基準日時を返事の時刻にする。
 * 元のケースにつなぎ（お客様の流れの次の時点）、生成は説明に残す。期待する対応・確認ポイントは運用が付ける
 */
export function continuationOf(
  parent: EvaluationCase,
  run: Pick<EvaluationRun, "id" | "body" | "caseVersion"> | null
): { draft: EvaluationCase; info: ContinuationInfo } {
  const sentAt = parent.scenario.asOf
  const replyAt = toJstIso(Date.parse(sentAt) + REPLY_DELAY_MS)
  const conversation = [
    ...parent.scenario.conversation,
    {
      at: sentAt,
      direction: "outbound" as const,
      contentType: "text" as const,
      body: run?.body ?? "",
    },
    { at: replyAt, direction: "inbound" as const, contentType: "text" as const, body: "" },
  ]
  return {
    draft: {
      id: 0,
      lockVersion: 0,
      title: continuationTitle(parent.title),
      description: run
        ? `検証ケース#${parent.id}（データ版 ${run.caseVersion + 1}）の生成 #${run.id} の続き。` +
          "AIドラフトを当社の送信として会話に足し、お客様の返事を受けた次の一手を確かめる。"
        : `検証ケース#${parent.id}（データ版 ${parent.lockVersion + 1}）の続き。` +
          "当社の送信とお客様の返事を会話に足し、次の一手を確かめる。",
      expectedTerminal: "",
      checkpoints: "",
      caseSet: INDIVIDUAL_CASE_SET,
      parentCaseId: parent.id,
      sourceDunningCaseId: parent.sourceDunningCaseId ?? null,
      sourceUserId: parent.sourceUserId ?? null,
      scenario: {
        ...carriedOver(parent.scenario, replyAt),
        asOf: replyAt,
        routingMode: "reactive",
        conversation,
        // 期待する道筋は会話が進むと変わるので引き継がない（期待する対応と同じく運用が付ける）
        expectedPath: undefined,
      },
    },
    info: {
      parentId: parent.id,
      parentTitle: parent.title,
      runId: run?.id ?? null,
      replyNumber: conversation.length,
    },
  }
}

/** 保存前の下書きの出どころ（画面に出す注意書きの切り替えに使う） */
export type DraftSource =
  { kind: "import"; preview: ImportPreview } | { kind: "continuation"; info: ContinuationInfo }

/** AI が選んだ対応（着地点）。人手対応は HUMAN_REVIEW、判断記録（decision.terminal）を優先し、無ければ観測の strategy.terminalId */
export const observedTerminal = (run: Pick<EvaluationRun, "generationMetadata">) => {
  const metadata = run.generationMetadata
  if (metadata?.humanReviewRequired) return "HUMAN_REVIEW"
  return metadata?.decision?.terminal || metadata?.strategy?.terminalId
}

/**
 * 期待と観測の突き合わせ。人の対応推奨（HUMAN_FOLLOW）は本文を I（1週間後の状況連絡）で作るので、
 * 観測 I は「本文の対応は一致」として食い違い扱いにしない（人の対応推奨の表示は観測に出ないため文面と合わせて確認する）
 */
export type TerminalMatch = "none" | "match" | "body_match" | "mismatch" | "unset"

export const terminalMatch = (
  run: Pick<EvaluationRun, "expectedTerminal" | "generationMetadata">
): TerminalMatch => {
  if (isUnsetTerminal(run.expectedTerminal)) return "unset"
  const observed = observedTerminal(run)
  if (!observed) return "none"
  if (observed === run.expectedTerminal) return "match"
  if (run.expectedTerminal === "HUMAN_FOLLOW" && observed === "I") return "body_match"
  return "mismatch"
}

/** 生成結果の一言：一致／不一致（未完了・失敗は処理の状態）。人の対応推奨で本文が I のものも一致に数える。期待が未定なら「—」 */
export const observationLabel = (run: EvaluationRun) => {
  if (run.status === "running") return "生成中"
  if (run.status === "failed") return "生成失敗"
  const match = terminalMatch(run)
  if (match === "unset") return "—"
  return match === "match" || match === "body_match" ? "一致" : "不一致"
}

export const dateLabel = (value: string) =>
  new Date(value).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })
